/**
 * Life4Earth V2 — Community Metadata API Adapter
 *
 * The single source of truth for community_entities,
 * community_entity_relationships, collections, collection_projects,
 * custom_entities, collection_schemas, collection_schema_fields, and
 * project_custom_metadata_values.
 *
 * DEV-932: replaced the legacy src/Data/Security/* and
 * src/Data/Collections/* flatfile reads. Those files no longer exist;
 * this module is the only path.
 *
 * Endpoint: /api/CommunityMetadata
 */

import { apiFetch } from "./client";
import { mediaSrc } from "./mediaUrl"; // tolerant image-src resolver for the cascaded logo
import { normalizeGuid } from "../utils/guid";
// DEV-877: eligible-projects endpoint returns the canonical ProjectResponse
// shape (including primary_owner_org_guid). Importing the type avoids drift
// between the two surfaces.
import type { ProjectResponse } from "./projects";
// DEV-969. Mutations in this file must invalidate the `community` cache
// key and notify subscribers so downstream views (schema dropdowns,
// hierarchy pickers, project metadata renderers) reflect the new state
// immediately instead of waiting for the cache's 60s natural stale time.
import { invalidateAndRefetch, QUERY_KEYS } from "./queryCache";

/**
 * apiFetch wrapper for mutations that affect the `community` cache key.
 * On success, invalidates the cache entry and dispatches CACHE_UPDATE_EVENT
 * so every subscriber (useApiCommunityData hook instances across the app)
 * triggers a refetch immediately. Reads continue to use bare apiFetch.
 *
 * Pattern: mutation functions below were changed from `apiFetch<T>(...)`
 * to `apiMutateCommunity<T>(...)`. Behaviorally identical on the request
 * itself; the only added work is the invalidate-and-notify call after
 * the response resolves.
 */
async function apiMutateCommunity<T>(
  path: string,
  options?: RequestInit,
): Promise<T> {
  const result = await apiFetch<T>(path, options);
  invalidateAndRefetch(QUERY_KEYS.community);
  return result;
}

// ── Response Types ──────────────────────────────────────────────

export interface CommunityEntityResponse {
  id: string;
  entityGuid: string;
  entityType: string;
  name: string;
  visibility: string;
  /**
   * DEV-1464. NOT the raw is_active column (vestigial). Server-computed:
   * "not dissolved AND not deleted". Guard on this before offering a community
   * as a joinable or selectable option anywhere.
   */
  isActive: boolean;
  /** DEV-1464. Non-null ⇒ dissolved: readable, inert, reversible. */
  dissolvedAt?: string | null;
  /** DEV-1464. Non-null ⇒ soft-deleted (only ever seen on the trash listing). */
  deletedAt?: string | null;
  code: string;
  createdAt: string;
  updatedAt: string;
  heroImageUrl?: string;
  logoUrl?: string;
  /** DEV-888. Human-readable URL slug; null when unset. */
  slug?: string | null;
  /**
   * DEV-961 / DEV-896 / ADR-006. Set when this community is the user's auto-
   * provisioned private workshop. Workshops are excluded from the
   * user-facing Communities navigation per the agreed-upon design.
   * Null for regular communities.
   *
   * Workshop-marker-only per POLICY_COMMUNITY_AUTHORITY §13. NOT a generic
   * owner field — geo/org administrative authority lives in V2
   * PermissionGrants on the CommunityAdmin bundle.
   */
  personalOwnerUserGuid?: string | null;
  /**
   * DEV-1245 P2 / POLICY_COMMUNITY_AUTHORITY §2.2 + §3. Structural fallback
   * authority for this community (the "guardian"). Renders as "Guarded by X"
   * on unadministered communities; routes bootstrap / recovery designation
   * to the right authority. NULL for Workshop entities (out of policy scope).
   */
  guardianEntityGuid?: string | null;
  /** DEV-968. Community info card — slot 1 address. NULL when unset. */
  address?: string | null;
  /** DEV-968. Community info card — slot 2 Tabler icon key (no `ti-` prefix). NULL when unset. */
  infoIconKey?: string | null;
  /** DEV-968. Community info card — slot 2 freeform text. NULL when unset. */
  infoText?: string | null;
  /** DEV-975. Optional primary-location reference. NULL when unset. */
  primaryLocationEntityGuid?: string | null;
  /**
   * DEV-980. Raw JSON string of per-tab visibility flags. Parse client-side.
   * Shape: {"news": false, "health": false, "forum": false, "media": false}.
   * NULL = legacy default (all tabs visible). Alignment and Feed are always
   * visible regardless. Elevated viewers (owner/admin/content_creator) see
   * all tabs regardless.
   */
  tabVisibility?: string | null;
  /** DEV-987. Master toggle for the News-tab cascade. Default true. */
  inheritNews?: boolean | null;
  /**
   * DEV-1190. Management tab visibility tier. One of 'public' | 'community'
   * | 'team'. Default 'community'. Capped by the row's own `visibility` —
   * private community cannot expose public Management.
   */
  managementVisibility?: string | null;
}

/**
 * DEV-888. Response shape from /api/communitymetadata/by-slug/{rootSlug}/{subSlug?}.
 * Includes resolved org-root context to make breadcrumb rendering trivial.
 */
export interface CommunityBySlugResponse {
  entityGuid: string;
  entityType: string;
  name: string;
  slug: string;
  orgRootGuid: string | null;
  orgRootSlug: string | null;
  orgRootName: string | null;
}

export interface CommunityEntityRelationshipResponse {
  id: string;
  parentEntityGuid: string;
  childEntityGuid: string;
  relationshipType: string;
  sortOrder: number;
}

export interface CollectionResponse {
  id: string;
  collectionGuid: string;
  collectionName: string;
  schemaGuid: string;
  communityGuid: string | null;
  description: string;
  ownerOrg: string;
  isActive: boolean;
  updatedAt: string;
}

export interface CollectionProjectResponse {
  id: string;
  collectionGuid: string;
  /**
   * DEV-1096. Polymorphic discriminator — "project" (default) or
   * "mini_project". Backward-compat with pre-DEV-1096 callers: rows
   * default to "project" server-side; older readers can treat any row
   * with this field absent as a project row.
   */
  memberType?: "project" | "mini_project";
  /** Nullable post-DEV-1096 — null for mini-project rows. */
  projectGuid: string | null;
  /** DEV-1096. Set for mini-project rows; null otherwise. */
  miniProjectGuid?: string | null;
}

export interface CustomEntityResponse {
  id: string;
  entityGuid: string;
  entityType: string;
  name: string;
  entityCode: string;
  parentEntityGuid: string | null;
  sortOrder: number;
  isActive: boolean;
}

export interface CollectionSchemaResponse {
  id: string;
  schemaGuid: string;
  schemaName: string;
  version: number;
  /** DEV-874 / ADR-004. NULL when isPlatform = true. */
  ownerOrgRootGuid: string | null;
  /** DEV-874 / ADR-004. Visible to every org when true. */
  isPlatform: boolean;
  /**
   * DEV-875. Per-schema count of distinct projects with at least one populated
   * value. Surfaced in the editor list to inform admins before destructive
   * edits. Only present on org-scoped read endpoints; legacy /schemas/all
   * returns 0.
   */
  projectsUsing: number;
  updatedAt: string;
}

export interface CollectionSchemaFieldResponse {
  id: string;
  schemaGuid: string;
  fieldGuid: string;
  fieldKey: string;
  label: string;
  type: string;
  groupable: boolean;
  sortable: boolean;
  searchable: boolean;
  missingPolicy: string;
  enumSetGuid: string | null;
  entityType: string | null;
}

export interface ProjectCustomMetadataValueResponse {
  id: string;
  /**
   * Task #215. Polymorphic discriminator — "project" (default), "mini_project",
   * or "chronicle". Pre-DEV-1096 readers can assume "project" when absent.
   */
  contentType?: "project" | "mini_project" | "chronicle";
  /** Nullable post-Task #215 — null on mini-project / chronicle rows. */
  projectGuid: string | null;
  /** Task #215. Set on mini-project rows; null otherwise. */
  miniProjectGuid?: string | null;
  /** Migration 081. Set on chronicle rows; null otherwise. */
  chronicleGuid?: string | null;
  fieldGuid: string;
  schemaGuid: string | null;
  collectionGuid: string | null;
  valueString: string | null;
  valueNumber: number | null;
  valueBoolean: boolean | null;
  valueDate: string | null;
  valueEnum: string | null;
  valueGuid: string | null;
}

// ── Shape Converters ────────────────────────────────────────────

/** Convert API community entity to seed-data shape */
export function communityEntityToSeedShape(e: CommunityEntityResponse): Record<string, any> {
  return {
    entity_guid: normalizeGuid(e.entityGuid),
    entity_type: e.entityType,
    name: e.name,
    visibility: e.visibility,
    // DEV-1464 / POLICY_COMMUNITY_DELETION §8.4. The server now COMPUTES
    // isActive as "not dissolved AND not deleted" (see ToCeResponse). Every
    // consumer that already guarded on `is_active !== false` before offering a
    // community as a joinable/selectable option is therefore correct for free.
    // Soft-deleted communities never reach here at all — a global EF query
    // filter excludes them.
    is_active: e.isActive,
    // Carried so surfaces can DISTINGUISH dissolved from merely inactive —
    // a dissolved community still renders (history is preserved, content stays
    // published) but must not be offered as a destination for new activity.
    dissolved_at: (e as any).dissolvedAt ?? null,
    code: e.code,
    created_at: e.createdAt,
    updated_at: e.updatedAt,
    hero_image_url: e.heroImageUrl ?? "",
    logo_url: e.logoUrl ?? "",
    // DEV-888: preserve slug through the seed-shape conversion. Without this,
    // GlobalSearch (and any other consumer of apiCommunityEntities) sees
    // slug=undefined on every entity and falls back to /communities/<guid>
    // routes even when slugs are set in the DB.
    slug: e.slug ?? null,
    // DEV-961: workshop back-link. Used to filter workshops out of the
    // user-facing Communities navigation per ADR-006.
    personal_owner_user_guid: e.personalOwnerUserGuid ?? null,
    // DEV-1245 P2: guardian reference (structural fallback authority).
    guardian_entity_guid: e.guardianEntityGuid ?? null,
    // DEV-968: community info card. Render guard is "any one populated";
    // empty string normalises to null for consumers.
    address: e.address ?? null,
    info_icon_key: e.infoIconKey ?? null,
    info_text: e.infoText ?? null,
    // DEV-975: primary location reference.
    primary_location_entity_guid: e.primaryLocationEntityGuid ?? null,
    // DEV-980: per-tab visibility flags (raw JSON for the consumer to parse).
    tab_visibility: e.tabVisibility ?? null,
    // DEV-987: News-cascade master toggle. Default true matches the DB column
    // default for any pre-migration row that lacks the field on the wire.
    inherit_news: e.inheritNews ?? true,
    // DEV-1190: Management tab visibility tier. Default 'community' matches
    // the DB column default for any pre-migration row.
    management_visibility: (e as any).managementVisibility ?? 'community',
  };
}

/** Convert API relationship to seed-data shape */
export function relationshipToSeedShape(r: CommunityEntityRelationshipResponse): Record<string, any> {
  return {
    parent_entity_guid: normalizeGuid(r.parentEntityGuid),
    child_entity_guid: normalizeGuid(r.childEntityGuid),
    relationship_type: r.relationshipType,
    sort_order: r.sortOrder,
  };
}

/** Convert API collection to seed-data shape */
export function collectionToSeedShape(c: CollectionResponse): Record<string, any> {
  return {
    collection_guid: normalizeGuid(c.collectionGuid),
    collection_name: c.collectionName,
    schema_guid: normalizeGuid(c.schemaGuid),
    community_guid: normalizeGuid(c.communityGuid),
    description: c.description,
    owner_org: c.ownerOrg,
    is_active: c.isActive,
    updated_at: c.updatedAt,
  };
}

/**
 * Convert API collection project to seed-data shape. DEV-1096: now
 * carries the polymorphic discriminator + mini_project_guid alongside
 * project_guid. Consumers that pre-date DEV-1096 only ever look at
 * project_guid, which is null for mini-project rows — that filters MP
 * rows out implicitly, which is exactly the safe legacy behavior. New
 * consumers (KpiGridTable membership filter, App.tsx Data aggregator)
 * should branch on `member_type` and pick the matching guid.
 */
export function collectionProjectToSeedShape(cp: CollectionProjectResponse): Record<string, any> {
  return {
    collection_guid: normalizeGuid(cp.collectionGuid),
    member_type: cp.memberType ?? "project",
    project_guid: cp.projectGuid ? normalizeGuid(cp.projectGuid) : null,
    mini_project_guid: cp.miniProjectGuid ? normalizeGuid(cp.miniProjectGuid) : null,
  };
}

/** Convert API custom entity to seed-data shape */
export function customEntityToSeedShape(e: CustomEntityResponse): Record<string, any> {
  return {
    entity_guid: normalizeGuid(e.entityGuid),
    entity_type: e.entityType,
    name: e.name,
    entity_code: e.entityCode,
    parent_entity_guid: normalizeGuid(e.parentEntityGuid),
    sort_order: e.sortOrder,
    is_active: e.isActive,
  };
}

/** Convert API schema to seed-data shape */
export function schemaToSeedShape(s: CollectionSchemaResponse): Record<string, any> {
  return {
    schema_guid: normalizeGuid(s.schemaGuid),
    schema_name: s.schemaName,
    version: s.version,
    updated_at: s.updatedAt,
  };
}

/** Convert API schema field to seed-data shape */
export function schemaFieldToSeedShape(f: CollectionSchemaFieldResponse): Record<string, any> {
  const result: Record<string, any> = {
    schema_guid: normalizeGuid(f.schemaGuid),
    field_guid: normalizeGuid(f.fieldGuid),
    field_key: f.fieldKey,
    label: f.label,
    type: f.type,
    groupable: f.groupable,
    sortable: f.sortable,
    searchable: f.searchable,
    missing_policy: f.missingPolicy,
  };
  if (f.enumSetGuid) result.enum_set_guid = normalizeGuid(f.enumSetGuid);
  if (f.entityType) result.entity_type = f.entityType;
  return result;
}

/** Convert API metadata value to seed-data shape */
export function metadataValueToSeedShape(v: ProjectCustomMetadataValueResponse): Record<string, any> {
  const result: Record<string, any> = {
    // Task #215. Carry the polymorphic discriminator + all three owner GUIDs
    // through the seed shape so consumers can branch on content_type. Legacy
    // consumers that only look at project_guid keep working — mini-project
    // rows land with project_guid=null and naturally filter out, matching the
    // pre-Task #215 collection_projects pattern.
    content_type: v.contentType ?? "project",
    project_guid: v.projectGuid ? normalizeGuid(v.projectGuid) : null,
    mini_project_guid: v.miniProjectGuid ? normalizeGuid(v.miniProjectGuid) : null,
    chronicle_guid: v.chronicleGuid ? normalizeGuid(v.chronicleGuid) : null,
    field_guid: normalizeGuid(v.fieldGuid),
  };
  if (v.schemaGuid) result.schema_guid = normalizeGuid(v.schemaGuid);
  if (v.collectionGuid) result.collection_guid = normalizeGuid(v.collectionGuid);
  if (v.valueString != null) result.value_string = v.valueString;
  if (v.valueNumber != null) result.value_number = v.valueNumber;
  if (v.valueBoolean != null) result.value_boolean = v.valueBoolean;
  if (v.valueDate != null) result.value_date = v.valueDate;
  if (v.valueEnum != null) result.value_enum = v.valueEnum;
  if (v.valueGuid != null) result.value_guid = normalizeGuid(v.valueGuid);
  return result;
}

// ── API Functions ────────────────────────────────────────────────

export async function fetchAllCommunityEntities(): Promise<CommunityEntityResponse[]> {
  return apiFetch<CommunityEntityResponse[]>("/api/CommunityMetadata/entities/all");
}

/** Fetch ALL community entities including inactive ones (admin use). */
export async function fetchAllCommunityEntitiesAdmin(): Promise<CommunityEntityResponse[]> {
  return apiFetch<CommunityEntityResponse[]>("/api/CommunityMetadata/entities/all?includeInactive=true");
}

/**
 * DEV-1032. Fetch ONE community entity by GUID, regardless of visibility.
 * Used by fetchOwnerDisplay to resolve project owners whose owning community
 * is private (and therefore filtered out of the bulk entities/all response
 * for the calling user). Returns null on 404 — orphan or missing entity.
 */
export async function fetchCommunityEntityByGuid(
  entityGuid: string,
): Promise<CommunityEntityResponse | null> {
  try {
    return await apiFetch<CommunityEntityResponse>(
      `/api/CommunityMetadata/entities/${entityGuid}`,
    );
  } catch {
    return null;
  }
}

export async function fetchAllRelationships(): Promise<CommunityEntityRelationshipResponse[]> {
  return apiFetch<CommunityEntityRelationshipResponse[]>("/api/CommunityMetadata/relationships/all");
}

export async function fetchAllCollections(communityGuid?: string): Promise<CollectionResponse[]> {
  const qs = communityGuid ? `?communityGuid=${encodeURIComponent(communityGuid)}` : "";
  return apiFetch<CollectionResponse[]>(`/api/CommunityMetadata/collections/all${qs}`);
}

export async function fetchAllCollectionProjects(): Promise<CollectionProjectResponse[]> {
  return apiFetch<CollectionProjectResponse[]>("/api/CommunityMetadata/collectionprojects/all");
}

export async function fetchAllCustomEntities(): Promise<CustomEntityResponse[]> {
  return apiFetch<CustomEntityResponse[]>("/api/CommunityMetadata/customentities/all");
}

export async function fetchAllSchemas(): Promise<CollectionSchemaResponse[]> {
  return apiFetch<CollectionSchemaResponse[]>("/api/CommunityMetadata/schemas/all");
}

export async function fetchAllSchemaFields(): Promise<CollectionSchemaFieldResponse[]> {
  return apiFetch<CollectionSchemaFieldResponse[]>("/api/CommunityMetadata/schemafields/all");
}

export async function fetchAllMetadataValues(): Promise<ProjectCustomMetadataValueResponse[]> {
  return apiFetch<ProjectCustomMetadataValueResponse[]>("/api/CommunityMetadata/metadatavalues/all");
}

// ── Community Entity CRUD ────────────────────────────────────────

export interface CreateCommunityEntityRequest {
  name: string;
  entityType: string;
  visibility?: string;
  code?: string;
  parentEntityGuid?: string;
  relationshipType?: string;
  /** DEV-888. Optional URL slug; auto-generated server-side from name if omitted. */
  slug?: string;
}

export interface UpdateCommunityEntityRequest {
  name?: string;
  entityType?: string;
  visibility?: string;
  code?: string;
  isActive?: boolean;
  heroImageUrl?: string;
  /**
   * DEV-895 / DEV-981. Community logo/avatar URL. Empty string clears
   * (community falls back to inherited cascade); non-empty sets the
   * community's own logo. NULL/undefined leaves unchanged.
   */
  logoUrl?: string;
  /** DEV-888. Empty string clears the slug; null/undefined leaves unchanged. */
  slug?: string;
  /** DEV-968. Empty string clears the address; null/undefined leaves unchanged. */
  address?: string;
  /**
   * DEV-968. Empty string clears the icon key. Non-empty must be one of:
   * clock, calendar-event, info-circle, phone, mail, world, users, building, star.
   * Server returns 400 for any other value.
   */
  infoIconKey?: string;
  /** DEV-968. Empty string clears the info text; null/undefined leaves unchanged. */
  infoText?: string;
  /**
   * DEV-975. Primary-location reference. Pass an empty string ("" — sent as
   * Guid.Empty on the wire) to clear; null/undefined leaves unchanged.
   */
  primaryLocationEntityGuid?: string;
  /**
   * DEV-980. Per-tab visibility flags as a raw JSON string. Empty string clears
   * (reverts to legacy all-visible default); valid JSON object sets;
   * null/undefined leaves unchanged. The server validates JSON shape.
   */
  tabVisibility?: string;
  /**
   * DEV-987. Master toggle for the News-tab cascade. TRUE = inherit approved
   * articles from ancestor communities. FALSE = own articles only. Omit to
   * leave unchanged.
   */
  inheritNews?: boolean;
  /**
   * DEV-1190. Management tab visibility tier. One of 'public' | 'community'
   * | 'team'. The server caps the value by the row's own `visibility`
   * (private community cannot expose public Management). Omit to leave
   * unchanged.
   */
  managementVisibility?: string;
}

/**
 * DEV-980. Configurable community-page tabs. Alignment and Feed are NOT
 * configurable — they are always visible. The keys here MUST match the
 * `TabKey` union in CommunityDetailPage.tsx for the relevant subset.
 */
export const CONFIGURABLE_COMMUNITY_TABS = ["news", "health", "forum", "media"] as const;
export type ConfigurableCommunityTab = (typeof CONFIGURABLE_COMMUNITY_TABS)[number];

/**
 * DEV-895 / DEV-981. Resolve a community's effective logo URL via the
 * inheritance cascade:
 *   1. The community's own `logo_url` (if set)
 *   2. The nearest ancestor with a non-empty `logo_url`
 *   3. NULL if neither the community nor any ancestor has a logo
 *
 * Both `entities` and `relationships` should come from the seed-shape
 * data (snake_case keys). Walking is bounded to 10 hops as a guard
 * against pathological data.
 *
 * Per DEV-895, this is the single primitive for resolving "what logo
 * should we display for this community-or-anything-owned-by-it." The
 * project page reads the owning community's effective logo via this
 * same helper.
 */
export function resolveEffectiveLogoUrl(
  communityGuid: string,
  entities: Array<Record<string, any>> | null | undefined,
  relationships: Array<Record<string, any>> | null | undefined,
): string | null {
  if (!communityGuid) return null;
  const entityMap = new Map<string, Record<string, any>>();
  for (const e of (entities || [])) {
    entityMap.set(String(e.entity_guid).toUpperCase(), e);
  }
  const parentMap = new Map<string, string>();
  for (const r of (relationships || [])) {
    parentMap.set(
      String(r.child_entity_guid).toUpperCase(),
      String(r.parent_entity_guid).toUpperCase(),
    );
  }
  let cursor: string | undefined = communityGuid.toUpperCase();
  for (let i = 0; i < 10 && cursor; i++) {
    const ent = entityMap.get(cursor);
    if (!ent) break;
    const url = (ent.logo_url ?? "").toString().trim();
    // Route the cascaded logo through the shared tolerant resolver so every
    // stored format (bare key, FileStorage path, double/host-prefixed, external)
    // renders correctly. mediaSrc is idempotent, so callers that re-wrap this
    // value (e.g. resolveAvatarUrl) are unaffected.
    if (url) return mediaSrc(url);
    cursor = parentMap.get(cursor);
  }
  return null;
}

/**
 * DEV-980. Parse a tab_visibility JSON string into a flat record. Missing
 * keys, malformed JSON, or non-object JSON all resolve to "all visible" (the
 * legacy default). Caller maps undefined/true → visible, false → hidden.
 */
export function parseTabVisibility(raw: string | null | undefined): Record<ConfigurableCommunityTab, boolean | undefined> {
  const empty: Record<ConfigurableCommunityTab, boolean | undefined> = {
    news: undefined, health: undefined, forum: undefined, media: undefined,
  };
  if (!raw) return empty;
  try {
    const obj = JSON.parse(raw);
    if (!obj || typeof obj !== "object" || Array.isArray(obj)) return empty;
    const out = { ...empty };
    for (const k of CONFIGURABLE_COMMUNITY_TABS) {
      const v = (obj as any)[k];
      if (typeof v === "boolean") out[k] = v;
    }
    return out;
  } catch {
    return empty;
  }
}

/**
 * DEV-968. Allowed icon keys for the community info card's second slot.
 * Mirrors the server-side palette in CommunityMetadataController.AllowedInfoIconKeys
 * and the DB CHECK constraint in migration 145. Stored without the `ti-`
 * prefix; the UI prepends it when rendering Tabler icons.
 */
export const COMMUNITY_INFO_ICON_KEYS = [
  "clock",
  "calendar-event",
  "info-circle",
  "phone",
  "mail",
  "world",
  "users",
  "building",
  "star",
] as const;

export type CommunityInfoIconKey = (typeof COMMUNITY_INFO_ICON_KEYS)[number];

export interface CreateRelationshipRequest {
  parentEntityGuid: string;
  childEntityGuid: string;
  relationshipType: string;
  sortOrder?: number;
}

/** Create a new community entity (optionally as child of parent). */
export async function createCommunityEntity(
  request: CreateCommunityEntityRequest
): Promise<CommunityEntityResponse> {
  return apiMutateCommunity<CommunityEntityResponse>("/api/CommunityMetadata/entities", {
    method: "POST",
    body: JSON.stringify(request),
  });
}

/** Update an existing community entity. */
export async function updateCommunityEntity(
  entityGuid: string,
  request: UpdateCommunityEntityRequest
): Promise<CommunityEntityResponse> {
  return apiMutateCommunity<CommunityEntityResponse>(
    `/api/CommunityMetadata/entities/${entityGuid}`,
    { method: "PUT", body: JSON.stringify(request) }
  );
}

/** Create a relationship between two entities. */
export async function createRelationship(
  request: CreateRelationshipRequest
): Promise<CommunityEntityRelationshipResponse> {
  return apiMutateCommunity<CommunityEntityRelationshipResponse>(
    "/api/CommunityMetadata/relationships",
    { method: "POST", body: JSON.stringify(request) }
  );
}

/** Remove a relationship between two entities. */
export async function removeRelationship(
  parentGuid: string,
  childGuid: string,
  relationshipType?: string
): Promise<void> {
  const qs = relationshipType ? `?relationshipType=${encodeURIComponent(relationshipType)}` : "";
  await apiMutateCommunity<void>(
    `/api/CommunityMetadata/relationships/${parentGuid}/${childGuid}${qs}`,
    { method: "DELETE" }
  );
}

// ── Collection CRUD ──────────────────────────────────────────────

export interface CreateCollectionRequest {
  collectionName: string;
  schemaGuid?: string;
  communityGuid?: string;
  description?: string;
  ownerOrg?: string;
}

export interface UpdateCollectionRequest {
  collectionName?: string;
  schemaGuid?: string;
  description?: string;
  ownerOrg?: string;
  isActive?: boolean;
}

/** Create a new collection. */
export async function createCollection(
  request: CreateCollectionRequest
): Promise<CollectionResponse> {
  return apiMutateCommunity<CollectionResponse>("/api/CommunityMetadata/collections", {
    method: "POST",
    body: JSON.stringify(request),
  });
}

/** Update an existing collection. */
export async function updateCollection(
  collectionGuid: string,
  request: UpdateCollectionRequest
): Promise<CollectionResponse> {
  return apiMutateCommunity<CollectionResponse>(
    `/api/CommunityMetadata/collections/${collectionGuid}`,
    {
      method: "PUT",
      body: JSON.stringify(request),
    }
  );
}

/** Remove a project from a collection. */
export async function removeProjectFromCollection(
  collectionGuid: string,
  projectGuid: string
): Promise<void> {
  await apiMutateCommunity<void>(
    `/api/CommunityMetadata/collectionprojects/${collectionGuid}/${projectGuid}`,
    { method: "DELETE" }
  );
}

/** Add a project to a collection (upsert — no-op if already linked).
 *
 * DEV-877: server enforces eligibility — the project's primary_owner_org_guid
 * (or one of its active partner-org-walked-roots) must match the collection's
 * owner-scope walked to org root. Returns 403 with code
 * `PROJECT_NOT_ELIGIBLE_FOR_COLLECTION` when the rule fails.
 *
 * DEV-1096: this function targets the Full-Project path explicitly. For
 * mini-projects, use addMiniProjectToCollection.
 */
export async function addProjectToCollection(
  collectionGuid: string,
  projectGuid: string
): Promise<CollectionProjectResponse> {
  return apiMutateCommunity<CollectionProjectResponse>("/api/CommunityMetadata/collectionprojects", {
    method: "POST",
    body: JSON.stringify({ collectionGuid, memberType: "project", projectGuid }),
  });
}

/**
 * DEV-1096. Add a Mini-Project to a collection (upsert — no-op if already
 * linked). Server enforces eligibility: the collection's owner-community
 * (or platform scope) must contain the MP. Returns 403 with code
 * `MINI_PROJECT_NOT_ELIGIBLE_FOR_COLLECTION` when the rule fails.
 */
export async function addMiniProjectToCollection(
  collectionGuid: string,
  miniProjectGuid: string,
): Promise<CollectionProjectResponse> {
  return apiMutateCommunity<CollectionProjectResponse>("/api/CommunityMetadata/collectionprojects", {
    method: "POST",
    body: JSON.stringify({ collectionGuid, memberType: "mini_project", miniProjectGuid }),
  });
}

/**
 * DEV-1096. Remove a Mini-Project from a collection. Sibling to
 * removeProjectFromCollection; routes through a distinct URL so the GUID
 * binder doesn't conflate a mini_project_guid with a project_guid.
 */
export async function removeMiniProjectFromCollection(
  collectionGuid: string,
  miniProjectGuid: string,
): Promise<void> {
  await apiMutateCommunity<void>(
    `/api/CommunityMetadata/collectionprojects/mini/${collectionGuid}/${miniProjectGuid}`,
    { method: "DELETE" },
  );
}

/**
 * DEV-1096. Eligible Mini-Projects for the Community Collections picker.
 * Server-side filter applies Skip's locked visibility rule: the MP shows
 * when its visibility is public/community OR the caller is on its team
 * or has an active role assignment. Already-attached MPs are excluded
 * unless `includeAttached` is true.
 */
export interface EligibleMiniProjectResponse {
  miniProjectGuid: string;
  title: string;
  shortDescription: string | null;
  visibility: string;
  status: string;
  ownerCommunityGuid: string;
  heroImageUrl: string | null;
  /** True when this MP only showed because the caller is on its team /
   *  role roster (i.e., visibility alone wouldn't have surfaced it). */
  viaMembership: boolean;
}

export async function fetchEligibleMiniProjectsForCollection(
  collectionGuid: string,
  options?: { includeAttached?: boolean },
): Promise<EligibleMiniProjectResponse[]> {
  const qs = options?.includeAttached ? "?includeAttached=true" : "";
  return apiFetch<EligibleMiniProjectResponse[]>(
    `/api/CommunityMetadata/collections/${collectionGuid}/eligible-mini-projects${qs}`,
  );
}

/**
 * DEV-877: list projects eligible to be added to the given collection. Returns
 * only active, currently-unattached projects whose org-eligibility set matches
 * the collection's owner-scope (walked to org root). Used by the Collections
 * tab project picker so the dropdown only shows valid candidates.
 *
 * The collection's owner-scope is walked to its org root before comparison,
 * so a collection scoped at District 7950 (sub-org of Rotary International)
 * eligibility-matches projects owned by Hampton Rotary Club too. The org tree
 * handles upward visibility.
 */
export async function fetchEligibleProjectsForCollection(
  collectionGuid: string,
  options?: { includeAttached?: boolean }
): Promise<ProjectResponse[]> {
  const qs = options?.includeAttached ? "?includeAttached=true" : "";
  return apiFetch<ProjectResponse[]>(
    `/api/CommunityMetadata/collections/${collectionGuid}/eligible-projects${qs}`
  );
}

// ── Project Custom Metadata Mutations (DEV-819) ──────────────────

/**
 * Upsert payload for a single project metadata value. Send exactly one typed
 * value column matching the field's type from collection_schema_fields. All
 * other typed columns should be omitted (or set to null) — the server overwrites
 * the row's typed columns from the request, so omitting a column clears it.
 *
 * Date values are ISO date strings ("YYYY-MM-DD") to match the server's
 * DateOnly binding.
 */
export interface UpsertProjectMetadataValueRequest {
  /**
   * Task #215. Optional explicit discriminator. Server infers from whichever
   * owner GUID is non-null (mini_project_guid → "mini_project",
   * chronicle_guid → "chronicle", default "project"). Setting it explicitly
   * is the defensive path when a caller somehow carries multiple GUIDs.
   */
  contentType?: "project" | "mini_project" | "chronicle";
  /** Required when contentType="project" (the default). */
  projectGuid?: string;
  /** Required when contentType="mini_project". */
  miniProjectGuid?: string;
  /** Required when contentType="chronicle". */
  chronicleGuid?: string;
  fieldGuid: string;
  schemaGuid?: string;
  collectionGuid?: string;
  valueString?: string | null;
  valueNumber?: number | null;
  valueBoolean?: boolean | null;
  valueDate?: string | null;
  valueEnum?: string | null;
  valueGuid?: string | null;
}

/**
 * Upsert a single metadata value for a project. Returns the persisted row
 * so the caller can update local state without a refetch. The server
 * validates that:
 *   - the field exists in the named schema
 *   - the project belongs to a collection using that schema
 *   - for guid-type fields, valueGuid resolves to a community entity with a
 *     matching entity_type
 * Validation failures return a 400 with a `message` body explaining the cause.
 */
export async function upsertProjectCustomMetadata(
  request: UpsertProjectMetadataValueRequest
): Promise<ProjectCustomMetadataValueResponse> {
  return apiMutateCommunity<ProjectCustomMetadataValueResponse>(
    "/api/CommunityMetadata/metadatavalues",
    { method: "POST", body: JSON.stringify(request) }
  );
}

/**
 * Clear all typed value columns on the metadata-value row, retaining the row
 * itself so the schema-field membership stays visible in the UI. Returns the
 * cleared row so the caller can update local state. 404 if no row exists for
 * the given (projectGuid, fieldGuid) pair.
 *
 * Named per the DEV-819 spec (`deleteProjectCustomMetadata`); the server
 * preserves the row by design — the action is "clear" rather than physical
 * delete. The schema-field is preserved so the user can re-enter a value
 * without first re-enrolling the project in the collection.
 */
export async function deleteProjectCustomMetadata(
  projectGuid: string,
  fieldGuid: string
): Promise<ProjectCustomMetadataValueResponse> {
  return apiMutateCommunity<ProjectCustomMetadataValueResponse>(
    `/api/CommunityMetadata/metadatavalues/${projectGuid}/${fieldGuid}`,
    { method: "DELETE" }
  );
}

/**
 * Batch upsert for saving an entire metadata form atomically. The server
 * validates every row before writing any of them, so partial failure is not
 * possible — either the whole batch persists or none of it does. Returns the
 * saved rows in the same order as the input.
 *
 * Although the server endpoint accepts rows for multiple projects in one
 * batch, the typical caller is a single-project metadata form. Cross-project
 * batches are technically supported but should be used judiciously.
 */
export async function batchUpsertProjectCustomMetadata(
  values: UpsertProjectMetadataValueRequest[]
): Promise<ProjectCustomMetadataValueResponse[]> {
  return apiMutateCommunity<ProjectCustomMetadataValueResponse[]>(
    "/api/CommunityMetadata/metadatavalues/batch",
    { method: "POST", body: JSON.stringify({ values }) }
  );
}

// ── Mini-Project Custom Metadata (Task #215) ─────────────────────
//
// Sibling helpers to the project-side functions above. The wire payload is
// identical; these functions just preset content_type="mini_project" and
// nudge callers toward the right owner GUID. Behind the scenes both hit the
// same /metadatavalues endpoints — only the discriminator differs.

/**
 * Task #215. Upsert a single metadata value for a mini-project. Returns the
 * persisted row. Server enforces eligibility: the MP must either be a member
 * of a collection using the schema OR have selected the schema directly via
 * mini_projects.metadata_schema_guid. See ValidateMetadataValueAsync.
 */
export async function upsertMiniProjectCustomMetadata(
  request: Omit<UpsertProjectMetadataValueRequest, "projectGuid" | "chronicleGuid" | "contentType"> & {
    miniProjectGuid: string;
  },
): Promise<ProjectCustomMetadataValueResponse> {
  return apiMutateCommunity<ProjectCustomMetadataValueResponse>(
    "/api/CommunityMetadata/metadatavalues",
    {
      method: "POST",
      body: JSON.stringify({ ...request, contentType: "mini_project" }),
    },
  );
}

/**
 * Task #215. Clear a single mini-project metadata-value row's typed columns
 * while retaining the row (parallel to deleteProjectCustomMetadata). Routes
 * to the MP-specific DELETE endpoint so the route binder never aliases an
 * MP guid as a project guid.
 */
export async function deleteMiniProjectCustomMetadata(
  miniProjectGuid: string,
  fieldGuid: string,
): Promise<ProjectCustomMetadataValueResponse> {
  return apiMutateCommunity<ProjectCustomMetadataValueResponse>(
    `/api/CommunityMetadata/metadatavalues/mini/${miniProjectGuid}/${fieldGuid}`,
    { method: "DELETE" },
  );
}

/**
 * Task #215. Batch upsert for saving an entire MP metadata form atomically.
 * Same semantics as batchUpsertProjectCustomMetadata — every row validates
 * before any row commits. Each row's contentType is stamped to "mini_project"
 * here so callers can supply just (miniProjectGuid, fieldGuid, value).
 */
export async function batchUpsertMiniProjectCustomMetadata(
  values: Array<
    Omit<UpsertProjectMetadataValueRequest, "projectGuid" | "chronicleGuid" | "contentType"> & {
      miniProjectGuid: string;
    }
  >,
): Promise<ProjectCustomMetadataValueResponse[]> {
  const stamped = values.map((v) => ({ ...v, contentType: "mini_project" as const }));
  return apiMutateCommunity<ProjectCustomMetadataValueResponse[]>(
    "/api/CommunityMetadata/metadatavalues/batch",
    { method: "POST", body: JSON.stringify({ values: stamped }) },
  );
}

// ── Org Level Taxonomy (DEV-865 / DEV-854) ───────────────────────

/**
 * One level in an organization's hierarchy taxonomy. Each row is identified
 * by (orgRootEntityGuid, levelKey). At instance creation time the level's
 * key becomes the suffix of entity_type: levelKey="rotary_district" produces
 * instances with entity_type="org_rotary_district".
 */
export interface OrgLevelTaxonomyResponse {
  id: string;
  orgRootEntityGuid: string;
  levelKey: string;
  levelLabel: string;
  parentLevelKey: string | null;
  sortOrder: number;
  isTerminal: boolean;
  /** Raw JSON text: e.g. '{"en":"Zone","fr":"Zone","es":"Zona"}'. */
  translations: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface CreateOrgLevelTaxonomyRequest {
  orgRootEntityGuid: string;
  levelKey: string;
  levelLabel: string;
  parentLevelKey?: string;
  sortOrder?: number;
  isTerminal?: boolean;
  translations?: string;
}

/**
 * Update payload. `levelKey` is intentionally absent — renaming a key would
 * orphan existing instances (entity_type='org_oldKey') and silently break
 * parent_level_key references. Use delete + create for renames after
 * reparenting/deleting dependents.
 */
export interface UpdateOrgLevelTaxonomyRequest {
  levelLabel?: string;
  /** Empty string means "set to null" (promote to root-level). */
  parentLevelKey?: string;
  sortOrder?: number;
  isTerminal?: boolean;
  translations?: string;
}

/** Fetch all level definitions for one org's hierarchy. */
export async function fetchOrgLevelTaxonomy(
  orgRootGuid: string
): Promise<OrgLevelTaxonomyResponse[]> {
  return apiFetch<OrgLevelTaxonomyResponse[]>(
    `/api/CommunityMetadata/org-level-taxonomy/${orgRootGuid}`
  );
}

/**
 * Fetch all taxonomies across every org. Frontend pre-loads this once at
 * app start so per-modal interactions don't need a round-trip to determine
 * which levels to show under a given org root.
 */
export async function fetchAllOrgLevelTaxonomies(): Promise<OrgLevelTaxonomyResponse[]> {
  return apiFetch<OrgLevelTaxonomyResponse[]>(
    "/api/CommunityMetadata/org-level-taxonomy"
  );
}

/** Create a new level in an org's taxonomy. */
export async function createOrgLevel(
  request: CreateOrgLevelTaxonomyRequest
): Promise<OrgLevelTaxonomyResponse> {
  return apiMutateCommunity<OrgLevelTaxonomyResponse>(
    "/api/CommunityMetadata/org-level-taxonomy",
    { method: "POST", body: JSON.stringify(request) }
  );
}

/** Update an existing level. */
export async function updateOrgLevel(
  id: string,
  request: UpdateOrgLevelTaxonomyRequest
): Promise<OrgLevelTaxonomyResponse> {
  return apiMutateCommunity<OrgLevelTaxonomyResponse>(
    `/api/CommunityMetadata/org-level-taxonomy/${id}`,
    { method: "PUT", body: JSON.stringify(request) }
  );
}

/**
 * Delete a level. Server refuses (400) if any other level references this
 * one as parent OR if any community_entity instance uses the corresponding
 * entity_type. Reparent or delete dependents first.
 */
export async function deleteOrgLevel(id: string): Promise<void> {
  await apiMutateCommunity<void>(
    `/api/CommunityMetadata/org-level-taxonomy/${id}`,
    { method: "DELETE" }
  );
}

// ════════════════════════════════════════════════════════════════
// DEV-874 / DEV-875 — Org-scoped schema editor API
// ════════════════════════════════════════════════════════════════
//
// These endpoints require community:manage_schemas (or community:admin
// fallback, or scoped-sysadmin) at the org root. The frontend only exposes
// the editor on org-root community contexts; non-org-root scopes will hit
// 400 from IsOrgRootEntity guard.
//
// Edit-semantics rules per ADR-004 §Decision 8:
//  - field_key is never editable (server has no path that accepts it)
//  - type changes return 409 with { error: "field_in_use" } when populated
//  - field/schema deletes return 409 with { error: "field_in_use" |
//    "schema_in_use" } when populated

export interface CreateOrgSchemaRequest {
  schemaName: string;
}

export interface UpdateSchemaRequest {
  schemaName?: string;
}

export interface CreateSchemaFieldRequest {
  fieldKey: string;
  label: string;
  /** One of: "string" | "number" | "boolean" | "date" | "enum" | "guid". */
  type: string;
  groupable: boolean;
  sortable: boolean;
  searchable: boolean;
  missingPolicy?: string;
  /** Required when type = "enum". */
  enumSetGuid?: string | null;
  /** Required when type = "guid". E.g. "org_rotary_district". */
  entityType?: string | null;
}

export interface UpdateSchemaFieldRequest {
  label?: string;
  type?: string;
  groupable?: boolean;
  sortable?: boolean;
  searchable?: boolean;
  missingPolicy?: string;
  enumSetGuid?: string | null;
  entityType?: string | null;
}

/**
 * List all schemas visible to the caller for a specific org root: schemas
 * owned by this org, plus platform-curated schemas. Each row carries a
 * `projectsUsing` count to inform destructive-edit decisions in the UI.
 */
export async function fetchSchemasForOrg(
  orgRootGuid: string
): Promise<CollectionSchemaResponse[]> {
  return apiFetch<CollectionSchemaResponse[]>(
    `/api/CommunityMetadata/orgs/${orgRootGuid}/schemas`
  );
}

/** Create a new org-owned schema. */
export async function createSchemaForOrg(
  orgRootGuid: string,
  request: CreateOrgSchemaRequest
): Promise<CollectionSchemaResponse> {
  return apiMutateCommunity<CollectionSchemaResponse>(
    `/api/CommunityMetadata/orgs/${orgRootGuid}/schemas`,
    { method: "POST", body: JSON.stringify(request) }
  );
}

/** Rename a schema. Other ownership/visibility fields are immutable here. */
export async function updateSchema(
  schemaGuid: string,
  request: UpdateSchemaRequest
): Promise<CollectionSchemaResponse> {
  return apiMutateCommunity<CollectionSchemaResponse>(
    `/api/CommunityMetadata/schemas/${schemaGuid}`,
    { method: "PUT", body: JSON.stringify(request) }
  );
}

/**
 * Delete a schema and its fields. Server returns 409 with
 * { error: "schema_in_use" } if any project has populated values.
 */
export async function deleteSchema(schemaGuid: string): Promise<void> {
  await apiMutateCommunity<void>(
    `/api/CommunityMetadata/schemas/${schemaGuid}`,
    { method: "DELETE" }
  );
}

/** Add a field to a schema. */
export async function addSchemaField(
  schemaGuid: string,
  request: CreateSchemaFieldRequest
): Promise<CollectionSchemaFieldResponse> {
  return apiMutateCommunity<CollectionSchemaFieldResponse>(
    `/api/CommunityMetadata/schemas/${schemaGuid}/fields`,
    { method: "POST", body: JSON.stringify(request) }
  );
}

/**
 * Update a field's facets and (optionally) type. Type changes are blocked
 * server-side with 409 when any value rows reference this field. The
 * `field_key` is intentionally absent — it's a stable identifier and is
 * never editable.
 */
export async function updateSchemaField(
  schemaGuid: string,
  fieldGuid: string,
  request: UpdateSchemaFieldRequest
): Promise<CollectionSchemaFieldResponse> {
  return apiMutateCommunity<CollectionSchemaFieldResponse>(
    `/api/CommunityMetadata/schemas/${schemaGuid}/fields/${fieldGuid}`,
    { method: "PUT", body: JSON.stringify(request) }
  );
}

/**
 * Delete a field. Returns 409 with { error: "field_in_use" } when populated.
 * Caller should surface the message to the admin and offer to null out
 * values first.
 */
export async function deleteSchemaField(
  schemaGuid: string,
  fieldGuid: string
): Promise<void> {
  await apiMutateCommunity<void>(
    `/api/CommunityMetadata/schemas/${schemaGuid}/fields/${fieldGuid}`,
    { method: "DELETE" }
  );
}

/**
 * Fetch the fields for a single schema. Reuses the bulk endpoint and filters
 * client-side for now; if usage grows, add a dedicated server endpoint.
 */
export async function fetchFieldsForSchema(
  schemaGuid: string
): Promise<CollectionSchemaFieldResponse[]> {
  const all = await fetchAllSchemaFields();
  return all.filter((f) => normalizeGuid(f.schemaGuid) === normalizeGuid(schemaGuid));
}

// ════════════════════════════════════════════════════════════════
// DEV-932 — Project Collection bulk replace
// ════════════════════════════════════════════════════════════════

export interface ReplaceProjectCollectionsRequest {
  collectionGuids: string[];
}

/**
 * Atomically replace a project's collection memberships with the desired
 * set. Server diffs current vs desired in a single transaction. If any new
 * attachment fails the eligibility gate (DEV-877), the entire request
 * returns 403 and no changes are applied. Returns the resulting links.
 */
export async function replaceProjectCollections(
  projectGuid: string,
  request: ReplaceProjectCollectionsRequest,
): Promise<CollectionProjectResponse[]> {
  return apiMutateCommunity<CollectionProjectResponse[]>(
    `/api/CommunityMetadata/projects/${projectGuid}/collections/replace`,
    { method: "POST", body: JSON.stringify(request) },
  );
}

// ════════════════════════════════════════════════════════════════
// DEV-920 — Schema hierarchies
// ════════════════════════════════════════════════════════════════
//
// First-class declaration of hierarchical relationships within a metadata
// schema. Replaces the hardcoded HIERARCHY_GROUP_OPTIONS constant in
// KpiGridTable. valueSource distinguishes metadata-value lookup (Rotary
// and other community hierarchies) from entity-walk lookup (Geography).

export interface SchemaHierarchyResponse {
  id: string;
  hierarchyGuid: string;
  schemaGuid: string;
  hierarchyKey: string;
  /**
   * Canonical user-facing name. Source of truth for the label everywhere
   * this hierarchy appears in the UI (Metadata tab picker label, Portfolio
   * Group By dropdown option, etc.). User data — never an i18n key.
   */
  displayName: string;
  /**
   * For valueSource='metadata_value': the lowest-tier field_guid (e.g.
   * rotary_club). For valueSource='entity_walk': null — the leaf is
   * implicit in levelOrder's last element.
   */
  leafFieldGuid: string | null;
  /**
   * JSON array string, ordered root → leaf.
   * valueSource='metadata_value': array of field_guid UUID strings.
   * valueSource='entity_walk': array of entity_type strings, e.g.
   *   ["geo_country","geo_state","geo_city"].
   * Callers parse on read.
   */
  levelOrder: string;
  /** "cascading" (default) | "unified_path" */
  presentation: string;
  /** "metadata_value" (default) | "entity_walk" */
  valueSource: string;
  updatedAt: string;
}

export interface CreateSchemaHierarchyRequest {
  hierarchyKey: string;
  displayName: string;
  leafFieldGuid?: string | null;
  /** JSON array string. See SchemaHierarchyResponse.levelOrder. */
  levelOrder: string;
  /** Defaults to "cascading" server-side. */
  presentation?: string;
  /** Defaults to "metadata_value" server-side. */
  valueSource?: string;
}

export interface UpdateSchemaHierarchyRequest {
  /**
   * Send undefined/omit to leave unchanged. hierarchyKey is NEVER editable
   * (stable identifier). When valueSource transitions to "entity_walk",
   * leafFieldGuid is auto-cleared server-side.
   */
  displayName?: string;
  leafFieldGuid?: string | null;
  levelOrder?: string;
  presentation?: string;
  valueSource?: string;
}

/** List all hierarchies declared on a schema. */
export async function fetchSchemaHierarchies(
  schemaGuid: string,
): Promise<SchemaHierarchyResponse[]> {
  return apiFetch<SchemaHierarchyResponse[]>(
    `/api/CommunityMetadata/schemas/${schemaGuid}/hierarchies`
  );
}

/**
 * List every hierarchy across all schemas. Used by the portfolio Group By
 * dropdown (Layer 4) to populate options without per-collection round trips.
 */
export async function fetchAllHierarchies(): Promise<SchemaHierarchyResponse[]> {
  return apiFetch<SchemaHierarchyResponse[]>(
    `/api/CommunityMetadata/hierarchies/all`
  );
}

/**
 * Create a new hierarchy on a schema. Server returns 409 with
 * { error: "hierarchy_key_taken" } if the hierarchyKey is already in use
 * within the schema. Returns 400 with a descriptive error key for
 * shape-validation failures (level_order_must_be_nonempty,
 * level_order_field_not_in_schema, etc.).
 */
export async function createSchemaHierarchy(
  schemaGuid: string,
  request: CreateSchemaHierarchyRequest,
): Promise<SchemaHierarchyResponse> {
  return apiMutateCommunity<SchemaHierarchyResponse>(
    `/api/CommunityMetadata/schemas/${schemaGuid}/hierarchies`,
    { method: "POST", body: JSON.stringify(request) }
  );
}

/**
 * Update a hierarchy. hierarchyKey is intentionally absent — it's a stable
 * identifier and is never editable. Switching valueSource to "entity_walk"
 * automatically clears leafFieldGuid server-side.
 */
export async function updateSchemaHierarchy(
  schemaGuid: string,
  hierarchyGuid: string,
  request: UpdateSchemaHierarchyRequest,
): Promise<SchemaHierarchyResponse> {
  return apiMutateCommunity<SchemaHierarchyResponse>(
    `/api/CommunityMetadata/schemas/${schemaGuid}/hierarchies/${hierarchyGuid}`,
    { method: "PUT", body: JSON.stringify(request) }
  );
}

/**
 * Delete a hierarchy declaration. No usage check is run server-side —
 * hierarchies are declarative; removing one stops Group By from offering
 * it but doesn't affect saved metadata values.
 */
export async function deleteSchemaHierarchy(
  schemaGuid: string,
  hierarchyGuid: string,
): Promise<void> {
  await apiMutateCommunity<void>(
    `/api/CommunityMetadata/schemas/${schemaGuid}/hierarchies/${hierarchyGuid}`,
    { method: "DELETE" }
  );
}

// ════════════════════════════════════════════════════════════════
// DEV-888 — Community URL slug resolution
// ════════════════════════════════════════════════════════════════

/**
 * Resolve a slug pair to a community entity. Pass only `rootSlug` to get the
 * org-root entity; pass `subSlug` too for a sub-level entity.
 *
 * Returns null on 404 (slug doesn't resolve) so callers can render a 404 page
 * cleanly without try/catch.
 */
export async function fetchCommunityBySlug(
  rootSlug: string,
  subSlug?: string,
): Promise<CommunityBySlugResponse | null> {
  const path = subSlug
    ? `/api/CommunityMetadata/by-slug/${encodeURIComponent(rootSlug)}/${encodeURIComponent(subSlug)}`
    : `/api/CommunityMetadata/by-slug/${encodeURIComponent(rootSlug)}`;
  try {
    return await apiFetch<CommunityBySlugResponse>(path);
  } catch (err: any) {
    // apiFetch throws on non-2xx. Treat 404 as null; rethrow other errors.
    if (err?.status === 404 || /404/.test(err?.message || "")) return null;
    throw err;
  }
}

/**
 * Slugify a name client-side for live URL preview. The server has its own
 * authoritative slugifier; this is for the editor preview only.
 */
export function slugifyName(name: string): string {
  return (name || "")
    .toLowerCase()
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "") // strip diacritics
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 64);
}

/**
 * DEV-947 — Build the canonical URL for a community entity.
 *
 * Returns the slug URL when the entity AND every visible ancestor up to a
 * URL top-level has a non-null slug; falls back to the GUID URL otherwise.
 *
 * `geo_planet` ancestors (Earth) are treated as transparent — they don't
 * appear in URLs so /usa/california/oakland is preferred over the longer
 * /earth/usa/california/oakland. Mirrors the resolver's `IsUrlTopLevelAsync`
 * logic in CommunityMetadataController.cs so generated URLs match what the
 * server expects to receive.
 *
 * Returns a real path (e.g. `/usa/california/oakland`) — DEV-940 migrated
 * the app from HashRouter to BrowserRouter, so callers pass the result to
 * `navigate(...)` or use it as the `to` prop of `<Link>` directly.
 *
 * @param entity         The leaf entity to construct the URL for
 * @param ancestorsRootToLeaf Ordered chain from root → immediate parent
 *                       (NOT including the entity itself). Pass [] for
 *                       a top-level entity.
 */
export function communityUrlFor(
  entity: { entityGuid: string; slug?: string | null; entityType?: string | null },
  ancestorsRootToLeaf: Array<{ slug?: string | null; entityType?: string | null }>,
): string {
  const guidFallback = `/communities/${entity.entityGuid}`;

  // Build full chain root → leaf
  const chain = [...ancestorsRootToLeaf, entity];

  // Skip geo_planet ancestors (Earth — transparent per DEV-947).
  // The leaf itself is never skipped even if somehow geo_planet (defensive).
  const visible = chain.filter((c, i) =>
    i === chain.length - 1 || (c.entityType ?? "").toLowerCase() !== "geo_planet"
  );

  // Every visible segment needs a non-empty slug; otherwise fall back.
  for (const c of visible) {
    if (!c.slug || !c.slug.trim()) {
      // DEV-947 followup: in dev mode, log which entity in the chain
      // lacked a slug. Helps diagnose "why is Hampton falling back to
      // GUID?" — the answer is always 'something in its ancestor chain
      // (or itself) hasn't been sluggified yet'. Silent in production.
      if (typeof import.meta !== "undefined" && (import.meta as any).env?.DEV) {
        const reasons = visible.map((x) => ({
          slug: x.slug ?? null,
          entityType: x.entityType ?? null,
          missing: !x.slug || !x.slug.trim(),
        }));
        // eslint-disable-next-line no-console
        console.warn(
          `[communityUrlFor] falling back to GUID for ${entity.entityGuid} — chain has missing slug(s):`,
          reasons,
        );
      }
      return guidFallback;
    }
  }

  return "/" + visible.map((c) => c.slug).join("/");
}

/**
 * DEV-947 — Walk a parent map to produce the root → leaf ancestor chain
 * for an entity. Convenience for nav sites that already have
 * `parentMap` (child → parent) and `entityByGuid` loaded — they can call
 * this then pass the result to `communityUrlFor`.
 *
 * Walks at most 16 levels (defensive bound against cycles in malformed
 * relationship data).
 */
export function walkAncestorChain<
  T extends { entityGuid: string; slug?: string | null; entityType?: string | null },
>(
  entity: T,
  parentMap: Map<string, string>,
  entityByGuid: Map<string, T>,
): T[] {
  const chain: T[] = [];
  let cursor: string | undefined = parentMap.get(entity.entityGuid.toUpperCase())
    ?? parentMap.get(entity.entityGuid.toLowerCase());
  let depth = 0;
  while (cursor && depth < 16) {
    const parent =
      entityByGuid.get(cursor.toUpperCase()) ?? entityByGuid.get(cursor.toLowerCase());
    if (!parent) break;
    chain.unshift(parent);
    cursor =
      parentMap.get(cursor.toUpperCase()) ?? parentMap.get(cursor.toLowerCase());
    depth++;
  }
  return chain;
}

/**
 * DEV-939 — Variable-depth slug path resolver.
 *
 * Pass an array of slug segments (e.g. ["usa", "california", "oakland"])
 * to resolve a deep path. The server walks the segments down the parent
 * chain: first segment must be a top-level entity, each subsequent
 * segment a child of the prior. Per-immediate-parent uniqueness for geo
 * entities means siblings under different parents may share a slug —
 * the path itself disambiguates.
 *
 * Returns null on 404 so callers can render a clean not-found page.
 */
export async function fetchCommunityBySlugPath(
  segments: string[],
): Promise<CommunityBySlugResponse | null> {
  const cleaned = (segments || [])
    .map((s) => (s || "").trim())
    .filter((s) => s.length > 0)
    .map((s) => encodeURIComponent(s));
  if (cleaned.length === 0) return null;
  const path = `/api/CommunityMetadata/by-slug-path/${cleaned.join("/")}`;
  try {
    return await apiFetch<CommunityBySlugResponse>(path);
  } catch (err: any) {
    if (err?.status === 404 || /404/.test(err?.message || "")) return null;
    throw err;
  }
}
